import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ResourceUri } from '@ngxp/rest';
import { doIfLoadingRequired, StateResource } from '@schir-int-client/ngrx-helpers';
import { Observable, Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import {
	askToDeleteVerfahrenAction,
	askToMarkVerfahrenAsAbgelehntAction,
	askToUnassignKontaktAction,
	assignUSignalAction,
	createVerfahrenAction,
	downloadAmtlicherAuszugAction,
	downloadAusdruckAction,
	downloadSchiffsbriefAction,
	downloadSchiffszertifikatAction,
	exportVerfahrenAction,
	getLastOpenedVerfahrenAction,
	loadVerfahrenAction,
	loadVerfahrenSingleAction,
	loadVerfahrenSingleSuccessAction,
	searchForVerfahrenAction,
	setVerfahrenPropertyAction,
	setZugeordneteVerfahrenAction,
	updateVerfahrenNotizAction,
	verfahrenBearbeitenAction,
	verfahrenBearbeitenBeendenAction,
	verfahrenUebernehmenAction,
} from './verfahren.actions';
import {
	NewVerfahren,
	RegisterAusdruckType,
	VerfahrenListResource,
	VerfahrenResource,
	VerfahrenSearchQuery,
} from './verfahren.model';
import {
	blattNummerByVerfahrenSelector,
	isAmtlicherAuszugDoneSelector,
	isVerfahrenSelectedSelector,
	verfahrenListAsArraySelector,
	verfahrenSearchBySelector,
	verfahrenSelector,
	verfahrenSingleSelector,
} from './verfahren.selectors';
import { VerfahrenRootState } from './verfahren.state';
import { setKontaktAssignmentAction, VerfahrenKontakt } from '@schir-int-client/adressverwaltung-shared';

@Injectable({ providedIn: 'root' })
export class VerfahrenFacade {
	readonly verfahrenCreatedSubject = new Subject<string>();

	constructor(private store: Store<VerfahrenRootState>) {}

	blattNummer: Observable<number> = this.store.select(blattNummerByVerfahrenSelector);

	verfahrenSingle: Observable<VerfahrenResource> = this.store.select(verfahrenSingleSelector);

	verfahren: Observable<StateResource<VerfahrenListResource>> = this.store
		.select(verfahrenSelector)
		.pipe(
			filter(
				verfahrenStateResource =>
					!doIfLoadingRequired(verfahrenStateResource, () => this.store.dispatch(loadVerfahrenAction()))
			)
		);

	verfahrenResultList = this.store.select(verfahrenListAsArraySelector);

	verfahrenSearchBy: Observable<VerfahrenSearchQuery> = this.store.select(verfahrenSearchBySelector);

	isVerfahrenSelected(verfahrenResource: VerfahrenResource): Observable<boolean> {
		return this.store.select(isVerfahrenSelectedSelector(verfahrenResource));
	}

	setSelectedVerfahren(verfahren: VerfahrenResource): void {
		this.verfahrenSingle.pipe(first()).subscribe(predecessor => {
			this.store.dispatch(loadVerfahrenSingleSuccessAction({ verfahren, predecessor }));
		});
	}

	loadSingleVerfahren(verfahrenUri: string) {
		this.store.dispatch(loadVerfahrenSingleAction({ verfahrenUri }));
	}

	searchVerfahren(query: VerfahrenSearchQuery, keepVerfahrenSingle: boolean = false): void {
		this.store.dispatch(searchForVerfahrenAction({ query, keepVerfahrenSingle }));
	}

	getLastOpenedVerfahren(keepVerfahrenSingle: boolean = false): void {
		this.store.dispatch(getLastOpenedVerfahrenAction({ keepVerfahrenSingle }));
	}

	downloadAusdruck(ausdruckType: RegisterAusdruckType): void {
		this.store.dispatch(downloadAusdruckAction({ ausdruckType }));
	}

	downloadAmtlicherAuszug(): Observable<StateResource<boolean>> {
		this.store.dispatch(downloadAmtlicherAuszugAction());
		return this.store.select(isAmtlicherAuszugDoneSelector);
	}

	downloadSchiffszertifikat(): void {
		this.store.dispatch(downloadSchiffszertifikatAction());
	}

	downloadSchiffsbrief(): void {
		this.store.dispatch(downloadSchiffsbriefAction());
	}

	exportVerfahren(url: string): void {
		this.store.dispatch(exportVerfahrenAction({ url }));
	}

	updateNotiz(notiz: string, verfahrenUri: ResourceUri): void {
		this.store.dispatch(updateVerfahrenNotizAction({ notiz, verfahrenUri }));
	}

	assignUSignal(verfahren: VerfahrenResource): void {
		this.store.dispatch(assignUSignalAction({ verfahren }));
	}

	createVerfahren(newVerfahren: NewVerfahren): void {
		this.store.dispatch(createVerfahrenAction({ newVerfahren }));
	}

	bearbeiten(): void {
		this.store.dispatch(verfahrenBearbeitenAction());
	}

	bearbeitenBeenden(): void {
		this.store.dispatch(verfahrenBearbeitenBeendenAction());
	}

	uebernehmen(): void {
		this.store.dispatch(verfahrenUebernehmenAction());
	}

	markAsAbgelehnt(verfahren: VerfahrenResource): void {
		this.store.dispatch(askToMarkVerfahrenAsAbgelehntAction({ verfahren }));
	}

	delete(verfahren: VerfahrenResource): void {
		this.store.dispatch(askToDeleteVerfahrenAction({ verfahren }));
	}

	isAmtlicherAuszugDone(): Observable<StateResource<boolean>> {
		return this.store.select(isAmtlicherAuszugDoneSelector);
	}

	unassignKontakt(verfahrenKontakt: VerfahrenKontakt) {
		const kontaktRemainsInVerfahren = verfahrenKontakt.kategorien.length > 0;
		const action = kontaktRemainsInVerfahren
			? setKontaktAssignmentAction({ verfahrenKontakt })
			: askToUnassignKontaktAction({ verfahrenKontakt });
		this.store.dispatch(action);
	}

	setVerfahrenProperty(verfahren: VerfahrenResource, linkRel: string, value: any) {
		this.store.dispatch(setVerfahrenPropertyAction({ verfahren, linkRel, value }));
	}

	setZugeordneteVerfahren(verfahren: VerfahrenResource, zugeordneteVerfahren: number[]) {
		this.store.dispatch(setZugeordneteVerfahrenAction({ verfahren, zugeordneteVerfahren }));
	}
}

export const NO_SEARCH_LIMIT = 10000;
